import React from 'react';
import { Box, Divider, Stack, Typography } from '@mui/material';
import type { QuoteFigures } from '../../utils/e2i';
import { formatAmount, formatPct } from '../../utils/e2i';

interface Props {
  figures: QuoteFigures;
  currency: string;
  vatCode: string | null;
  vatRate: number;
  chargeReason?: string | null;
  /** A preview of unsaved changes (the server works the amounts out again on save). */
  preview?: boolean;
}

const Row: React.FC<{ label: React.ReactNode; value: string; strong?: boolean; muted?: boolean }> = ({ label, value, strong, muted }) => (
  <Stack direction="row" spacing={2} justifyContent="space-between">
    <Typography variant="body2" color={muted ? 'text.secondary' : 'text.primary'}>{label}</Typography>
    <Typography variant="body2" fontWeight={strong ? 700 : 400} color={muted ? 'text.secondary' : 'text.primary'} sx={{ whiteSpace: 'nowrap' }}>{value}</Typography>
  </Stack>
);

/** The quotation's totals, and for the team the cost and the margin. */
const QuotationFigures: React.FC<Props> = ({ figures: f, currency, vatCode, vatRate, chargeReason, preview }) => {
  const money = (n: number) => `${formatAmount(n)} ${currency}`.trim();
  const marginPct = f.subtotal ? (f.margin / f.subtotal) * 100 : 0;
  return (
    <Box sx={{ minWidth: { sm: 300 }, maxWidth: 420, ml: { sm: 'auto' } }}>
      <Stack spacing={0.5}>
        <Row label="Lines" value={money(f.linesTotal)} />
        {f.charge ? <Row label={`Extra charge${chargeReason ? ` (${chargeReason})` : ''}`} value={money(f.charge)} /> : null}
        {f.discount ? <Row label="Discount" value={`− ${money(f.discount)}`} /> : null}
        <Row label="Subtotal" value={money(f.subtotal)} strong />
        <Row label={`VAT${vatCode ? ` ${vatCode}` : ''} (${formatPct(vatRate)})`} value={money(f.vat)} />
        <Divider />
        <Row label="Total" value={money(f.total)} strong />
        {f.cost ? (
          <>
            <Divider />
            <Row label="Cost (from the cost lines)" value={money(f.cost)} muted />
            <Row label="Margin" value={`${money(f.margin)} · ${formatPct(Math.round(marginPct * 10) / 10)}`} muted />
          </>
        ) : null}
        {preview ? <Typography variant="caption" color="text.secondary">Preview of unsaved changes: saved amounts are worked out by the server.</Typography> : null}
      </Stack>
    </Box>
  );
};

export default QuotationFigures;
